"use client"

import {
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"
import { FitAddon } from "@xterm/addon-fit"
import { Terminal as XTerm } from "@xterm/xterm"
import "@xterm/xterm/css/xterm.css"
import { Readline } from "xterm-readline"
import {
  GripHorizontal,
  Maximize2,
  Minimize,
  Minimize2,
  ArrowDownToLine,
  PanelRightClose,
  PanelRightOpen,
  X as XClose,
} from "lucide-react"

import { createClient } from "@/lib/supabase/client"
import { getCharacterPlatformBackendUrl } from "@/lib/backend/server"
import { chatSend, chatSendStream, chatReset } from "@/lib/backend/chat"
import {
  shouldSkipPointerDragStart,
  startPointerSession,
} from "@/lib/pointer-drag"
import { ZoomOriginPanel } from "./zoom-origin-panel"

const PROMPT = "openclaw> "
const BUILT_IN_COMMANDS = [
  "help",
  "commands",
  "status",
  "clear",
  "chat",
  "chat-sse",
]

/** Built-in command descriptor. */
type BuiltInDef = {
  help: string
  run: (args: string[]) => void | Promise<void>
}

/** Build the chat-mode prompt string. */
function chatPrompt(agentId: string | null, streaming: boolean) {
  const mode = streaming ? "chat-sse" : "chat"
  return agentId ? `${mode} [${agentId}]> ` : `${mode}> `
}

type OpenClawTerminalCommand = {
  name: string
  help?: string | null
  usage: string
}

type OpenClawTerminalInfoResponse = {
  connected: boolean
  platform_key: string
  commands: OpenClawTerminalCommand[]
}

type OpenClawTerminalExecuteResponse = {
  command: string
  exit_code: number
  stdout: string
  stderr: string
  ok: boolean
  connected: boolean
}

type ReadlineState = {
  line: {
    buf: string
    pos: number
    update: (text: string, pos: number) => void
    set_pos: (pos: number) => void
  }
  tty: { write: (text: string) => void }
  refresh: () => void
  editBackspace: (count: number) => void
  moveCursor: () => void
  moveCursorToEnd: () => void
  previousHistory: () => void
  nextHistory: () => void
  __cursorVisibilityPatched?: boolean
}

type PatchedReadline = {
  activeRead?: unknown
  handleKeyEvent?: (event: KeyboardEvent) => boolean
  readData?: (data: string) => void
  state?: ReadlineState
  read: (prompt: string) => Promise<string>
  __cursorVisibilityPatched?: boolean
}

function normalizeTerminalText(value: string) {
  return value.replace(/\r?\n/g, "\r\n")
}

export type AutoMessage = { agentId: string | null; text: string }

function isWordChar(char: string) {
  return /[A-Za-z0-9_]/.test(char)
}

function previousWordBoundary(text: string, pos: number) {
  let nextPos = pos

  while (nextPos > 0 && /\s/.test(text[nextPos - 1] ?? "")) {
    nextPos -= 1
  }

  while (nextPos > 0 && isWordChar(text[nextPos - 1] ?? "")) {
    nextPos -= 1
  }

  while (
    nextPos > 0 &&
    !/\s/.test(text[nextPos - 1] ?? "") &&
    !isWordChar(text[nextPos - 1] ?? "")
  ) {
    nextPos -= 1
  }

  return nextPos
}

function nextWordBoundary(text: string, pos: number) {
  let nextPos = pos

  while (nextPos < text.length && /\s/.test(text[nextPos] ?? "")) {
    nextPos += 1
  }

  while (nextPos < text.length && isWordChar(text[nextPos] ?? "")) {
    nextPos += 1
  }

  while (
    nextPos < text.length &&
    !/\s/.test(text[nextPos] ?? "") &&
    !isWordChar(text[nextPos] ?? "")
  ) {
    nextPos += 1
  }

  return nextPos
}

function moveCursorToPosition(state: ReadlineState, nextPos: number) {
  if (nextPos === state.line.pos) return
  state.line.set_pos(nextPos)
  state.moveCursor()
}

function replaceBufferRange(
  state: ReadlineState,
  start: number,
  end: number,
  nextCursorPos: number
) {
  if (start === end) return
  const nextBuffer = state.line.buf.slice(0, start) + state.line.buf.slice(end)
  state.line.update(nextBuffer, nextCursorPos)
  state.refresh()
}

function patchReadlineCursorVisibility(readline: Readline) {
  const patchedReadline = readline as unknown as PatchedReadline
  if (patchedReadline.__cursorVisibilityPatched) return

  const originalRead = patchedReadline.read.bind(readline)
  const originalHandleKeyEvent = patchedReadline.handleKeyEvent?.bind(readline)
  const originalReadData = patchedReadline.readData?.bind(readline)
  let hiddenCursorDepth = 0

  const withHiddenCursor = (state: ReadlineState, operation: () => void) => {
    hiddenCursorDepth += 1
    if (hiddenCursorDepth === 1) {
      state.tty.write("\x1b[?25l")
    }

    try {
      operation()
    } finally {
      hiddenCursorDepth -= 1
      if (hiddenCursorDepth === 0) {
        state.tty.write("\x1b[?25h")
      }
    }
  }

  patchedReadline.read = (prompt: string) => {
    const result = originalRead(prompt)
    const state = patchedReadline.state

    if (state && !state.__cursorVisibilityPatched) {
      const originalRefresh = state.refresh.bind(state)
      const originalEditBackspace = state.editBackspace.bind(state)
      const originalMoveCursor = state.moveCursor.bind(state)
      const originalMoveCursorToEnd = state.moveCursorToEnd.bind(state)

      state.refresh = () => withHiddenCursor(state, originalRefresh)
      state.editBackspace = (count: number) =>
        withHiddenCursor(state, () => originalEditBackspace(count))
      state.moveCursor = () => withHiddenCursor(state, originalMoveCursor)
      state.moveCursorToEnd = () =>
        withHiddenCursor(state, originalMoveCursorToEnd)
      state.__cursorVisibilityPatched = true
    }

    return result
  }

  if (originalHandleKeyEvent) {
    patchedReadline.handleKeyEvent = (event: KeyboardEvent) => {
      const state = patchedReadline.state
      if (event.type === "keydown" && state && patchedReadline.activeRead) {
        const key = event.key.toLowerCase()
        const isWordLeft =
          key === "arrowleft" &&
          ((event.altKey && !event.ctrlKey) ||
            (event.ctrlKey && !event.altKey)) &&
          !event.metaKey
        const isWordRight =
          key === "arrowright" &&
          ((event.altKey && !event.ctrlKey) ||
            (event.ctrlKey && !event.altKey)) &&
          !event.metaKey

        if (isWordLeft) {
          moveCursorToPosition(
            state,
            previousWordBoundary(state.line.buf, state.line.pos)
          )
          return false
        }

        if (isWordRight) {
          moveCursorToPosition(
            state,
            nextWordBoundary(state.line.buf, state.line.pos)
          )
          return false
        }

        if (event.ctrlKey && !event.altKey && !event.metaKey) {
          if (key === "b") {
            moveCursorToPosition(
              state,
              Math.max(0, state.line.pos > 0 ? state.line.pos - 1 : 0)
            )
            return false
          }
          if (key === "f") {
            moveCursorToPosition(
              state,
              Math.min(state.line.buf.length, state.line.pos + 1)
            )
            return false
          }
          if (key === "p") {
            state.previousHistory()
            return false
          }
          if (key === "n") {
            state.nextHistory()
            return false
          }
          if (key === "w") {
            const start = previousWordBoundary(state.line.buf, state.line.pos)
            replaceBufferRange(state, start, state.line.pos, start)
            return false
          }
        }

        if (event.altKey && !event.ctrlKey && !event.metaKey) {
          if (key === "b") {
            moveCursorToPosition(
              state,
              previousWordBoundary(state.line.buf, state.line.pos)
            )
            return false
          }
          if (key === "f") {
            moveCursorToPosition(
              state,
              nextWordBoundary(state.line.buf, state.line.pos)
            )
            return false
          }
          if (key === "d") {
            const end = nextWordBoundary(state.line.buf, state.line.pos)
            replaceBufferRange(state, state.line.pos, end, state.line.pos)
            return false
          }
          if (key === "backspace") {
            const start = previousWordBoundary(state.line.buf, state.line.pos)
            replaceBufferRange(state, start, state.line.pos, start)
            return false
          }
        }
      }

      return originalHandleKeyEvent(event)
    }
  }

  if (originalReadData) {
    patchedReadline.readData = (data: string) => {
      const state = patchedReadline.state
      if (state && patchedReadline.activeRead) {
        if (data === "\x1b[1;3D" || data === "\x1b[1;5D") {
          moveCursorToPosition(
            state,
            previousWordBoundary(state.line.buf, state.line.pos)
          )
          return
        }

        if (data === "\x1b[1;3C" || data === "\x1b[1;5C") {
          moveCursorToPosition(
            state,
            nextWordBoundary(state.line.buf, state.line.pos)
          )
          return
        }
      }

      originalReadData(data)
    }
  }

  patchedReadline.__cursorVisibilityPatched = true
}

export function OpenClawTerminal({
  onClose,
  title = "OpenClaw Terminal",
  startDetached = false,
  autoMessage,
  hideCatalog = false,
  visible = true,
  originRect = null,
  animationMs = 280,
}: {
  onClose?: () => void
  title?: string
  startDetached?: boolean
  autoMessage?: AutoMessage
  hideCatalog?: boolean
  visible?: boolean
  originRect?: DOMRect | null
  animationMs?: number
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const terminalRef = useRef<XTerm | null>(null)
  const readlineRef = useRef<Readline | null>(null)
  const supabaseRef = useRef(createClient())
  const executingRef = useRef(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const promptLoopIdRef = useRef(0)

  // Chat mode — tracked in refs to avoid re-renders on every keystroke
  const chatModeRef = useRef(false)
  const chatAgentIdRef = useRef<string | null>(null)
  const chatStreamingRef = useRef(false)

  const [connected, setConnected] = useState(false)
  const [platformKey, setPlatformKey] = useState("")
  const [commands, setCommands] = useState<OpenClawTerminalCommand[]>([])
  const [loadingInfo, setLoadingInfo] = useState(true)
  const [activeCommand, setActiveCommand] = useState<string | null>(null)

  // Floating window state
  const [detached, setDetached] = useState(startDetached)
  const [minimized, setMinimized] = useState(false)
  const [floatPos, setFloatPos] = useState(() => {
    if (startDetached && typeof window !== "undefined") {
      const w = Math.min(Math.max(480, window.innerWidth * 0.68), 1080)
      const h = Math.min(Math.max(240, window.innerHeight * 0.36), 560) + 44
      return {
        x: Math.max(16, (window.innerWidth - w) / 2),
        y: Math.max(16, (window.innerHeight - h) / 2),
      }
    }
    return { x: 120, y: 120 }
  })
  const [floatWidthPx, setFloatWidthPx] = useState<number | null>(null)
  const [floatTermHeightPx, setFloatTermHeightPx] = useState<number | null>(
    null
  )
  const [isMaximized, setIsMaximized] = useState(false)
  const [catalogVisible, setCatalogVisible] = useState(() => !hideCatalog)
  const preMaximizeRef = useRef<{
    pos: { x: number; y: number }
    widthPx: number | null
    termHeightPx: number | null
  } | null>(null)

  // Refs for portal window positioning
  const placeholderRef = useRef<HTMLDivElement>(null)
  const portalWindowRef = useRef<HTMLDivElement>(null)
  const fitAddonRef = useRef<FitAddon | null>(null)
  const initialAutoMessageRef = useRef(autoMessage)
  const [winRect, setWinRect] = useState({ left: 0, top: 80, width: 800 })
  const [placeholderHeight, setPlaceholderHeight] = useState(0)
  const [windowHeight, setWindowHeight] = useState(0)

  const write = useEffectEvent((text: string) => {
    readlineRef.current?.write(normalizeTerminalText(text))
  })

  const writeln = useEffectEvent((text = "") => {
    readlineRef.current?.println(normalizeTerminalText(text))
  })

  const refitTerminal = useEffectEvent(() => {
    fitAddonRef.current?.fit()
  })

  const enterChatMode = useEffectEvent(
    (agentId: string | null, streaming: boolean) => {
      chatModeRef.current = true
      chatAgentIdRef.current = agentId
      chatStreamingRef.current = streaming
      writeln()
      writeln(
        `\x1b[36mEntering ${streaming ? "streaming " : ""}chat mode${agentId ? ` with agent "${agentId}"` : " with default agent"}.\x1b[0m`
      )
      writeln(
        `\x1b[2mType your message and press Enter. Use /exit to return to the terminal.\x1b[0m`
      )
      writeln(`\x1b[2mUse /reset to clear the conversation history.\x1b[0m`)
      writeln()
    }
  )

  const exitChatMode = useEffectEvent(() => {
    chatModeRef.current = false
    chatAgentIdRef.current = null
    chatStreamingRef.current = false
    writeln()
    writeln("\x1b[36mExited chat mode.\x1b[0m")
    writeln()
  })

  const printCatalog = useEffectEvent(() => {
    writeln(`Built-ins: ${BUILT_IN_COMMANDS.join(", ")}`)
    writeln(`RPC commands: ${commands.length}`)
    for (const command of commands) {
      writeln(`  ${command.name.padEnd(24)} ${command.help ?? ""}`)
    }
  })

  const printStatus = useEffectEvent((info: OpenClawTerminalInfoResponse) => {
    writeln(
      `Connection: ${info.connected ? "connected" : "waiting for OpenClaw instance"}`
    )
    writeln(`Platform key: ${info.platform_key}`)
    writeln(`Commands: ${info.commands.length}`)
  })

  const getAccessToken = useEffectEvent(async () => {
    const {
      data: { session },
    } = await supabaseRef.current.auth.getSession()

    return session?.access_token ?? null
  })

  const fetchTerminalInfo = useEffectEvent(async () => {
    const accessToken = await getAccessToken()
    if (!accessToken) {
      throw new Error("No active session found.")
    }

    const response = await fetch(
      new URL("/v1/openclaw/terminal", getCharacterPlatformBackendUrl()),
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        cache: "no-store",
      }
    )

    if (!response.ok) {
      throw new Error("Failed to load terminal metadata.")
    }

    return (await response.json()) as OpenClawTerminalInfoResponse
  })

  const refreshInfo = useEffectEvent(async (announce = false) => {
    const info = await fetchTerminalInfo()
    setConnected(info.connected)
    setPlatformKey(info.platform_key)
    setCommands(info.commands)

    if (announce) {
      printStatus(info)
    }

    return info
  })

  const runRemoteCommand = useEffectEvent(async (command: string) => {
    const accessToken = await getAccessToken()
    if (!accessToken) {
      writeln("Authentication required. Sign in again and retry.")
      return
    }

    const controller = new AbortController()
    abortControllerRef.current = controller
    executingRef.current = true
    setActiveCommand(command)

    try {
      const response = await fetch(
        new URL(
          "/v1/openclaw/terminal/execute",
          getCharacterPlatformBackendUrl()
        ),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ command }),
          cache: "no-store",
          signal: controller.signal,
        }
      )

      if (!response.ok) {
        let detail = "Command failed."

        try {
          const payload = (await response.json()) as { detail?: string }
          if (payload.detail) {
            detail = payload.detail
          }
        } catch {
          detail = await response.text()
        }

        writeln(detail)
        return
      }

      const payload = (await response.json()) as OpenClawTerminalExecuteResponse

      if (payload.stdout) {
        write(payload.stdout)
        if (!payload.stdout.endsWith("\n")) {
          writeln()
        }
      }

      if (payload.stderr) {
        write(`\x1b[31m${payload.stderr}\x1b[0m`)
        if (!payload.stderr.endsWith("\n")) {
          writeln()
        }
      }

      if (!payload.ok) {
        writeln(`Exited with code ${payload.exit_code}`)
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        writeln("^C")
        writeln("Command cancelled.")
      } else {
        writeln(
          error instanceof Error ? error.message : "Unknown terminal error."
        )
      }
    } finally {
      abortControllerRef.current = null
      executingRef.current = false
      setActiveCommand(null)
    }
  })

  // ---------------------------------------------------------------------------
  // Chat message send implementations
  // ---------------------------------------------------------------------------

  const sendChatMessageBlocking = useEffectEvent(
    async (accessToken: string, line: string) => {
      const controller = new AbortController()
      abortControllerRef.current = controller
      executingRef.current = true
      try {
        write("\x1b[2m…\x1b[0m")
        const result = await chatSend(accessToken, line, chatAgentIdRef.current)
        write("\r\x1b[2K")
        writeln(
          result.text
            ? `\x1b[32magent>\x1b[0m ${result.text}`
            : "\x1b[2m(no response)\x1b[0m"
        )
      } catch (err) {
        write("\r\x1b[2K")
        if (err instanceof DOMException && err.name === "AbortError") {
          writeln("\x1b[31mAborted.\x1b[0m")
        } else {
          writeln(
            `\x1b[31m${err instanceof Error ? err.message : "Unknown error."}\x1b[0m`
          )
        }
      } finally {
        abortControllerRef.current = null
        executingRef.current = false
      }
    }
  )

  const sendChatMessageStreaming = useEffectEvent(
    async (accessToken: string, line: string) => {
      const controller = new AbortController()
      abortControllerRef.current = controller
      executingRef.current = true

      const SPINNER = ["⠋", "⠙", "⠹", "⠸", "⠼", "⠴", "⠦", "⠧", "⠇", "⠏"]
      let spinnerIdx = 0
      let spinnerTimer: ReturnType<typeof setInterval> | null = null

      const startSpinner = () => {
        terminalRef.current?.write(`\x1b[2m${SPINNER[0]}\x1b[0m`)
        spinnerTimer = setInterval(() => {
          spinnerIdx = (spinnerIdx + 1) % SPINNER.length
          terminalRef.current?.write(`\b\x1b[2m${SPINNER[spinnerIdx]}\x1b[0m`)
        }, 80)
      }

      const stopSpinner = () => {
        if (spinnerTimer === null) return
        clearInterval(spinnerTimer)
        spinnerTimer = null
        terminalRef.current?.write("\b \b")
      }

      try {
        write("\x1b[32magent>\x1b[0m ")
        startSpinner()
        let hasOutput = false
        await chatSendStream(
          accessToken,
          line,
          chatAgentIdRef.current,
          (text) => {
            if (!hasOutput) {
              stopSpinner()
              hasOutput = true
            }
            write(text)
          },
          controller.signal
        )
        stopSpinner()
        if (!hasOutput) write("\x1b[2m(no response)\x1b[0m")
        writeln()
      } catch (err) {
        stopSpinner()
        writeln()
        if (err instanceof DOMException && err.name === "AbortError") {
          writeln("\x1b[31mAborted.\x1b[0m")
        } else {
          writeln(
            `\x1b[31m${err instanceof Error ? err.message : "Unknown error."}\x1b[0m`
          )
        }
      } finally {
        abortControllerRef.current = null
        executingRef.current = false
      }
    }
  )

  const sendChatMessage = useEffectEvent(async (rawLine: string) => {
    const line = rawLine.trim()

    if (!line) {
      return
    }

    if (line === "/exit" || line === "/quit") {
      exitChatMode()
      return
    }

    if (line === "/reset") {
      const accessToken = await getAccessToken()
      if (!accessToken) {
        writeln("\x1b[31mAuthentication required.\x1b[0m")
        return
      }
      try {
        await chatReset(accessToken, chatAgentIdRef.current)
        writeln("\x1b[2mConversation history cleared.\x1b[0m")
      } catch (err) {
        writeln(
          `\x1b[31m${err instanceof Error ? err.message : "Failed to reset."}\x1b[0m`
        )
      }
      return
    }

    const accessToken = await getAccessToken()
    if (!accessToken) {
      writeln(
        "\x1b[31mAuthentication required. Sign in again and retry.\x1b[0m"
      )
      return
    }

    if (chatStreamingRef.current) {
      await sendChatMessageStreaming(accessToken, line)
    } else {
      await sendChatMessageBlocking(accessToken, line)
    }
  })

  // ---------------------------------------------------------------------------
  // Built-in command dispatch
  // ---------------------------------------------------------------------------

  const executeLine = useEffectEvent(async (rawLine: string) => {
    const line = rawLine.trim()

    if (!line) {
      return
    }

    const [cmd = "", ...args] = line.split(/\s+/)

    const builtins: Record<string, BuiltInDef> = {
      clear: {
        help: "Clear the terminal screen",
        run: () => {
          terminalRef.current?.clear()
        },
      },
      help: {
        help: "Show this help",
        run: () => {
          writeln("Built-in commands:")
          for (const [name, def] of Object.entries(builtins)) {
            writeln(`  ${name.padEnd(16)} ${def.help}`)
          }
          writeln()
          writeln("RPC commands (forwarded to OpenClaw):")
          printCatalog()
        },
      },
      commands: {
        help: "List all RPC commands",
        run: () => printCatalog(),
      },
      status: {
        help: "Show connection status",
        run: async () => {
          try {
            printStatus(await refreshInfo())
          } catch (err) {
            writeln(
              err instanceof Error ? err.message : "Failed to refresh status."
            )
          }
        },
      },
      chat: {
        help: "Interactive chat (blocking — full reply before display)",
        run: ([agentId]) => enterChatMode(agentId ?? null, false),
      },
      "chat-sse": {
        help: "Interactive chat (streaming — tokens appear as they arrive)",
        run: ([agentId]) => enterChatMode(agentId ?? null, true),
      },
    }

    const def = builtins[cmd]
    if (def) {
      await def.run(args)
      return
    }

    await runRemoteCommand(line)
  })

  const currentPrompt = useEffectEvent(() =>
    chatModeRef.current
      ? chatPrompt(chatAgentIdRef.current, chatStreamingRef.current)
      : PROMPT
  )

  const readNextLine = useEffectEvent(async (loopId: number) => {
    const rl = readlineRef.current
    const term = terminalRef.current
    if (!rl || !term || loopId !== promptLoopIdRef.current) return

    try {
      const line = await rl.read(currentPrompt())
      if (loopId !== promptLoopIdRef.current) return
      if (chatModeRef.current) {
        await sendChatMessage(line)
      } else {
        await executeLine(line)
      }
    } catch (error) {
      if (loopId !== promptLoopIdRef.current) return
      writeln(
        error instanceof Error
          ? error.message
          : "Failed to read terminal input."
      )
    }

    if (
      loopId === promptLoopIdRef.current &&
      terminalRef.current &&
      readlineRef.current
    ) {
      void readNextLine(loopId)
    }
  })

  const restartPromptLoop = useEffectEvent(() => {
    promptLoopIdRef.current += 1
    void readNextLine(promptLoopIdRef.current)
  })

  useEffect(() => {
    if (!containerRef.current || terminalRef.current) {
      return
    }

    const term = new XTerm({
      cursorBlink: true,
      cursorStyle: "block",
      // Concrete monospace stack: xterm's canvas glyph measurements don't
      // resolve CSS variables, and --font-geist-mono isn't defined in this
      // app anyway, so referencing it made measurement and rendering fall
      // back to different fonts and produced misaligned glyphs.
      fontFamily:
        "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
      fontSize: 13,
      lineHeight: 1.25,
      convertEol: true,
      theme: {
        background: "#060606",
        foreground: "#f5f5f5",
        cursor: "#fb923c",
        selectionBackground: "rgba(251, 146, 60, 0.2)",
        black: "#0a0a0a",
        red: "#f87171",
        green: "#4ade80",
        yellow: "#facc15",
        blue: "#60a5fa",
        magenta: "#f472b6",
        cyan: "#22d3ee",
        white: "#fafafa",
        brightBlack: "#525252",
        brightRed: "#fca5a5",
        brightGreen: "#86efac",
        brightYellow: "#fde047",
        brightBlue: "#93c5fd",
        brightMagenta: "#f9a8d4",
        brightCyan: "#67e8f9",
        brightWhite: "#ffffff",
      },
    })
    const fitAddon = new FitAddon()
    const readline = new Readline()
    fitAddonRef.current = fitAddon
    readlineRef.current = readline

    patchReadlineCursorVisibility(readline)
    term.loadAddon(fitAddon)
    term.loadAddon(readline)
    term.open(containerRef.current)
    fitAddon.fit()
    readline.setCheckHandler(() => true)
    readline.setCtrlCHandler(() => {
      if (executingRef.current) {
        abortControllerRef.current?.abort()
      }
    })

    terminalRef.current = term
    term.writeln("CharacterPlatform OpenClaw Terminal")
    term.writeln(
      "Type `help` for built-ins or run any OpenClaw client command."
    )
    term.writeln("")

    const resize = () => refitTerminal()
    window.addEventListener("resize", resize)

    // Prevent Ctrl+W from closing the browser tab while the terminal is focused.
    const preventCtrlW = (e: KeyboardEvent) => {
      if (
        e.ctrlKey &&
        e.key === "w" &&
        term.element?.contains(document.activeElement)
      ) {
        e.preventDefault()
      }
    }
    window.addEventListener("keydown", preventCtrlW, { capture: true })

    return () => {
      promptLoopIdRef.current += 1
      window.removeEventListener("resize", resize)
      window.removeEventListener("keydown", preventCtrlW, { capture: true })
      readlineRef.current = null
      fitAddon.dispose()
      fitAddonRef.current = null
      term.dispose()
      terminalRef.current = null
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadInfo = async () => {
      try {
        const info = await refreshInfo()
        if (cancelled) return
        writeln()
        writeln(`Session ready for platform key ${info.platform_key}.`)
        writeln(
          info.connected
            ? "Live instance detected. Remote commands are enabled."
            : "No live instance yet. You can still browse commands with `commands`."
        )
        const initialAutoMessage = initialAutoMessageRef.current
        if (!cancelled && initialAutoMessage) {
          // Enter streaming chat mode and send the setup message automatically
          enterChatMode(initialAutoMessage.agentId, true)
          await sendChatMessage(initialAutoMessage.text)
        }
      } catch (error) {
        if (cancelled) return
        writeln(
          error instanceof Error
            ? error.message
            : "Failed to initialize terminal."
        )
      } finally {
        if (!cancelled) {
          setLoadingInfo(false)
          restartPromptLoop()
        }
      }
    }

    void loadInfo()

    return () => {
      cancelled = true
    }
  }, [])

  // Track the placeholder's viewport position so the portal can overlay it when docked.
  useLayoutEffect(() => {
    if (detached) return
    const measure = () => {
      if (!placeholderRef.current) return
      const r = placeholderRef.current.getBoundingClientRect()
      setWinRect({ left: r.left, top: r.top, width: r.width })
    }
    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [detached])

  // Refit XTerm whenever the window moves between docked/floating or minimized state.
  useEffect(() => {
    const id = setTimeout(() => refitTerminal(), 50)
    return () => clearTimeout(id)
  }, [detached, minimized, catalogVisible])

  // Refit xterm whenever the user resizes the floating window.
  useEffect(() => {
    const id = setTimeout(() => refitTerminal(), 50)
    return () => clearTimeout(id)
  }, [floatWidthPx, floatTermHeightPx])

  const handleTitlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (shouldSkipPointerDragStart(e)) return

    let startWinX: number
    let startWinY: number

    if (detached) {
      startWinX = floatPos.x
      startWinY = floatPos.y
    } else {
      // Docked window is full-width. When detaching, position the floating
      // window so the cursor stays at the same horizontal offset, clamped to
      // keep it within the actual floating window width (mirrors windowStyle).
      const floatWidth = Math.min(
        Math.max(480, window.innerWidth * 0.68),
        1080,
        window.innerWidth - 32
      )
      const clickOffsetX = e.clientX - winRect.left
      const clampedOffsetX = Math.min(clickOffsetX, floatWidth - 40)
      startWinX = e.clientX - clampedOffsetX
      startWinY = winRect.top
    }

    const startMouseX = e.clientX
    const startMouseY = e.clientY
    let didDetach = detached

    startPointerSession(e, {
      onMove: (nextEvent) => {
        const dx = nextEvent.clientX - startMouseX
        const dy = nextEvent.clientY - startMouseY
        if (!didDetach && Math.hypot(dx, dy) > 10) {
          didDetach = true
          setDetached(true)
        }
        if (didDetach) {
          setFloatPos({
            x: Math.max(0, Math.min(window.innerWidth - 300, startWinX + dx)),
            y: Math.max(0, Math.min(window.innerHeight - 44, startWinY + dy)),
          })
        }
      },
    })
  }

  const seedCommand = (command: string) => {
    terminalRef.current?.paste(command)
    terminalRef.current?.focus()
  }

  const handleResizePointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    edge:
      | "left"
      | "right"
      | "top"
      | "bottom"
      | "topLeft"
      | "topRight"
      | "bottomLeft"
      | "bottomRight"
  ) => {
    e.stopPropagation()
    const startMouseX = e.clientX
    const startMouseY = e.clientY
    const startX = floatPos.x
    const startY = floatPos.y
    const startWidth = portalWindowRef.current?.offsetWidth ?? 600
    const startTermHeight = containerRef.current?.parentElement?.offsetHeight ?? 300

    startPointerSession(e, {
      onMove: (nextEvent) => {
        const dx = nextEvent.clientX - startMouseX
        const dy = nextEvent.clientY - startMouseY
        if (
          edge === "right" ||
          edge === "topRight" ||
          edge === "bottomRight"
        ) {
          setFloatWidthPx(Math.max(360, startWidth + dx))
        }
        if (
          edge === "left" ||
          edge === "topLeft" ||
          edge === "bottomLeft"
        ) {
          const nextWidth = Math.max(360, startWidth - dx)
          const consumedDx = startWidth - nextWidth
          setFloatWidthPx(nextWidth)
          setFloatPos((prev) => ({ ...prev, x: startX + consumedDx }))
        }
        if (
          edge === "bottom" ||
          edge === "bottomLeft" ||
          edge === "bottomRight"
        ) {
          setFloatTermHeightPx(Math.max(160, startTermHeight + dy))
        }
        if (
          edge === "top" ||
          edge === "topLeft" ||
          edge === "topRight"
        ) {
          const nextHeight = Math.max(160, startTermHeight - dy)
          const consumedDy = startTermHeight - nextHeight
          setFloatTermHeightPx(nextHeight)
          setFloatPos((prev) => ({ ...prev, y: startY + consumedDy }))
        }
      },
    })
  }

  const handleTitleDoubleClick = () => {
    if (!detached) return
    if (!isMaximized) {
      preMaximizeRef.current = {
        pos: floatPos,
        widthPx: floatWidthPx,
        termHeightPx: floatTermHeightPx,
      }
      const m = 8
      setFloatPos({ x: m, y: m })
      setFloatWidthPx(window.innerWidth - m * 2)
      setFloatTermHeightPx(Math.max(200, window.innerHeight - m * 2 - 112))
      setIsMaximized(true)
    } else {
      const saved = preMaximizeRef.current
      if (saved) {
        setFloatPos(saved.pos)
        setFloatWidthPx(saved.widthPx)
        setFloatTermHeightPx(saved.termHeightPx)
      }
      setIsMaximized(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Portal window — always rendered at document.body to keep XTerm alive.
  // In docked mode it overlays the placeholder; in detached mode it floats.
  // ---------------------------------------------------------------------------

  const windowStyle: React.CSSProperties = detached
    ? {
        position: "fixed",
        left: floatPos.x,
        top: floatPos.y,
        width: floatWidthPx
          ? `${floatWidthPx}px`
          : "clamp(480px, 68vw, 1080px)",
        maxWidth: "calc(100vw - 16px)",
        zIndex: 10001,
      }
    : {
        position: "fixed",
        left: 0,
        top: winRect.top,
        width: "100vw",
        zIndex: 10000,
      }

  const estimatedWindowHeight =
    windowHeight ||
    (typeof window !== "undefined"
      ? Math.min(Math.max(340, window.innerHeight * 0.36 + 92), 720)
      : 420)

  const estimatedDetachedWidth =
    floatWidthPx ||
    (typeof window !== "undefined"
      ? Math.min(Math.max(480, window.innerWidth * 0.68), 1080)
      : 800)

  const targetRect = detached
    ? {
        left: floatPos.x,
        top: floatPos.y,
        width: estimatedDetachedWidth,
        height: estimatedWindowHeight,
      }
    : {
        left: 0,
        top: winRect.top,
        width:
          typeof window !== "undefined"
            ? window.innerWidth
            : winRect.width || 1,
        height: estimatedWindowHeight,
      }

  const handlePanelHeightChange = useCallback(
    (nextHeight: number) => {
      setWindowHeight(nextHeight)
      if (!detached) {
        setPlaceholderHeight(nextHeight)
      }
    },
    [detached]
  )

  // Terminal pane height: user-resized value or default CSS clamp.
  const terminalHeight = floatTermHeightPx
    ? `${floatTermHeightPx}px`
    : "clamp(240px, 36vh, 560px)"

  const statusBadges = (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span
        className={`rounded-none border px-2.5 py-1 ${
          connected
            ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300"
            : "border-amber-500/30 bg-amber-500/10 text-amber-200"
        }`}
      >
        {loadingInfo
          ? "Checking connection..."
          : connected
            ? "Instance connected"
            : "Waiting for instance"}
      </span>
      {activeCommand ? (
        <span className="rounded-none border border-primary/30 bg-primary/10 px-2.5 py-1 text-primary">
          Running: {activeCommand}
        </span>
      ) : null}
    </div>
  )

  const portalContent = (
    <ZoomOriginPanel
      ref={portalWindowRef}
      visible={visible}
      originRect={originRect}
      targetRect={targetRect}
      animationMs={animationMs}
      onHeightChange={handlePanelHeightChange}
      style={windowStyle}
      className={
        detached
          ? "relative overflow-hidden rounded-lg border border-border bg-background shadow-[0_32px_64px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.06)]"
          : "border-t border-border bg-background/95 backdrop-blur"
      }
    >
      {/* ── Title / drag bar ── */}
      <div
        className="flex cursor-grab items-center justify-between border-b border-white/6 bg-white/2 px-4 py-2 select-none active:cursor-grabbing"
        onPointerDown={handleTitlePointerDown}
        onDoubleClick={handleTitleDoubleClick}
        style={{ touchAction: "none" }}
      >
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <GripHorizontal className="h-3.5 w-3.5 opacity-40" />
          <span className="font-medium text-foreground/70">{title}</span>
          {!detached && <span className="opacity-40">— drag to detach</span>}
        </div>
        <div className="flex items-center gap-0.5">
          {detached && statusBadges}
          {detached && (
            <>
              <button
                type="button"
                onClick={handleTitleDoubleClick}
                className="ml-2 rounded p-1 text-muted-foreground transition-colors hover:bg-white/8 hover:text-foreground"
                title={isMaximized ? "Restore" : "Maximize"}
              >
                {isMaximized ? (
                  <Minimize2 className="h-3.5 w-3.5" />
                ) : (
                  <Maximize2 className="h-3.5 w-3.5" />
                )}
              </button>
              <button
                type="button"
                onClick={() => setMinimized((m) => !m)}
                className="rounded p-1 text-muted-foreground transition-colors hover:bg-white/8 hover:text-foreground"
                title={minimized ? "Restore" : "Minimize"}
              >
                <Minimize className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setDetached(false)
                  setMinimized(false)
                }}
                className="rounded p-1 text-muted-foreground transition-colors hover:bg-white/8 hover:text-foreground"
                title="Snap back to dock"
              >
                <ArrowDownToLine className="h-3.5 w-3.5" />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-white/8 hover:text-foreground"
            title="Close terminal"
          >
            <XClose className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* ── Body — hidden via CSS when minimized to keep XTerm mounted ── */}
      <div
        className={
          detached ? "p-4" : "mx-auto w-full max-w-7xl px-4 py-4 md:px-8"
        }
        style={{ display: minimized ? "none" : undefined }}
      >
        {!detached && (
          <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
            {statusBadges}
          </div>
        )}

        <div
          className={
            !hideCatalog && catalogVisible
              ? "grid items-start gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(280px,0.9fr)]"
              : undefined
          }
        >
          <div className="overflow-hidden rounded-none border border-border bg-black/80 shadow-[0_0_0_1px_rgba(255,255,255,0.03)]">
            <div className="flex items-center justify-between border-b border-white/8 bg-white/3 px-4 py-2 text-xs text-muted-foreground">
              <span>Interactive command surface</span>
              <div className="flex items-center gap-2">
                <span>
                  {platformKey
                    ? `platform ${platformKey.slice(0, 10)}...`
                    : "No platform key"}
                </span>
                {!hideCatalog && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={() => setCatalogVisible((v) => !v)}
                    className="rounded p-0.5 text-muted-foreground/60 transition-colors hover:text-muted-foreground"
                    title={catalogVisible ? "Hide catalog" : "Show catalog"}
                  >
                    {catalogVisible ? (
                      <PanelRightClose className="h-3.5 w-3.5" />
                    ) : (
                      <PanelRightOpen className="h-3.5 w-3.5" />
                    )}
                  </button>
                )}
              </div>
            </div>
            <div
              className="w-full pt-2 pr-4 pb-1 pl-3"
              style={{ height: terminalHeight }}
            >
              <div
                ref={containerRef}
                className="h-full w-full"
                style={{ letterSpacing: "normal" }}
                aria-label="OpenClaw terminal"
              />
            </div>
          </div>

          {!hideCatalog && catalogVisible && (
            <div
              className="flex flex-col rounded-none border border-border bg-card/30"
              style={{ height: `calc(${terminalHeight} + 35px)` }}
            >
              <div className="shrink-0 border-b border-border px-4 py-3">
                <h3 className="text-sm font-semibold text-foreground">
                  Command Catalog
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Click a command to stage it in the terminal prompt.
                </p>
              </div>
              <div className="flex-1 space-y-2 overflow-y-auto p-3">
                {commands.map((command) => (
                  <button
                    key={command.name}
                    type="button"
                    onClick={() => seedCommand(command.name)}
                    className="block w-full rounded-none border border-border bg-background/40 px-3 py-2 text-left transition-colors hover:border-primary/30 hover:bg-primary/8"
                  >
                    <div className="font-mono text-xs text-primary">
                      {command.name}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {command.help ?? command.usage}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Resize handles (detached only, not maximized) ── */}
      {detached && !isMaximized && (
        <>
          <div
            className="absolute top-0 left-0 h-1.5 w-full cursor-ns-resize"
            onPointerDown={(e) => handleResizePointerDown(e, "top")}
            style={{ touchAction: "none" }}
          />
          <div
            className="absolute top-0 left-0 h-full w-1.5 cursor-ew-resize"
            onPointerDown={(e) => handleResizePointerDown(e, "left")}
            style={{ touchAction: "none" }}
          />
          <div
            className="absolute top-0 right-0 h-full w-1.5 cursor-ew-resize"
            onPointerDown={(e) => handleResizePointerDown(e, "right")}
            style={{ touchAction: "none" }}
          />
          <div
            className="absolute bottom-0 left-0 h-1.5 w-full cursor-ns-resize"
            onPointerDown={(e) => handleResizePointerDown(e, "bottom")}
            style={{ touchAction: "none" }}
          />
          <div
            className="absolute top-0 left-0 h-3 w-3 cursor-nwse-resize"
            onPointerDown={(e) => handleResizePointerDown(e, "topLeft")}
            style={{ touchAction: "none" }}
          />
          <div
            className="absolute top-0 right-0 h-3 w-3 cursor-nesw-resize"
            onPointerDown={(e) => handleResizePointerDown(e, "topRight")}
            style={{ touchAction: "none" }}
          />
          <div
            className="absolute bottom-0 left-0 h-3 w-3 cursor-nesw-resize"
            onPointerDown={(e) => handleResizePointerDown(e, "bottomLeft")}
            style={{ touchAction: "none" }}
          />
          <div
            className="absolute right-0 bottom-0 h-3 w-3 cursor-nwse-resize"
            onPointerDown={(e) => handleResizePointerDown(e, "bottomRight")}
            style={{ touchAction: "none" }}
          />
        </>
      )}
    </ZoomOriginPanel>
  )

  return (
    <>
      {/* Placeholder reserves space in the navbar when docked */}
      <div
        ref={placeholderRef}
        style={{ height: detached ? 0 : placeholderHeight }}
      />
      {typeof window !== "undefined" &&
        createPortal(portalContent, document.body)}
    </>
  )
}
